import { useEffect, useState } from "react";
import { api, extractErrorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Snowflake, Flame, Loader2 } from "lucide-react";

/** Streak-About-to-Break Rescue Banner.
 *
 * Renders ONLY when:
 *   - current_streak >= 3 (a meaningful streak to save)
 *   - active_today is false (the student hasn't studied yet today)
 *
 * Offers two affordances:
 *   1. "Save my streak" CTA → links to Smart Review
 *   2. "❄️ Use weekly freeze" → POSTs to /student/streak/freeze (if available)
 *
 * After a successful freeze, fires `onFrozen()` so parent can refetch stats.
 */
export default function StreakRescueBanner({ stats, onFrozen }) {
  const current = stats?.streak?.current_streak ?? 0;
  const activeToday = stats?.streak?.active_today ?? false;
  const eligible = current >= 3 && !activeToday;

  const [freezeStatus, setFreezeStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!eligible) return;
    api.get("/student/streak/freeze-status")
      .then((r) => setFreezeStatus(r.data))
      .catch(() => setFreezeStatus(null));
  }, [eligible]);

  if (!eligible) return null;

  const handleFreeze = async () => {
    setBusy(true);
    try {
      const r = await api.post("/student/streak/freeze");
      toast.success(`Streak saved! ❄️`, {
        description: `Your ${current}-day streak is safe — your weekly freeze covered yesterday.`,
        duration: 7000,
      });
      setFreezeStatus({ ...freezeStatus, weekly_freeze_available: false });
      onFrozen?.(r.data);
    } catch (e) {
      toast.error(extractErrorMessage(e, "Couldn't apply freeze"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="rounded-xl p-4 border-2 border-rose-300 bg-gradient-to-r from-rose-50 via-orange-50 to-amber-50 flex items-start gap-3 shadow-sm"
      data-testid="streak-rescue-banner"
    >
      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-rose-500 to-orange-500 text-white grid place-items-center shrink-0 mt-0.5">
        <Flame className="w-5 h-5" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-display font-bold text-base text-rose-900">
          Save your {current}-day streak!
        </div>
        <div className="text-sm text-rose-800/90 mt-0.5">
          Answer at least one question today, or use your weekly freeze to keep the chain alive.
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          <a href="/student/smart-review">
            <Button
              size="sm"
              className="bg-gradient-to-r from-orange-500 to-rose-500 hover:opacity-90 text-white shadow"
              data-testid="streak-rescue-study-btn"
            >
              <Flame className="w-4 h-4 mr-1.5" /> Save my streak
            </Button>
          </a>
          {freezeStatus?.weekly_freeze_available ? (
            <Button
              size="sm"
              variant="outline"
              onClick={handleFreeze}
              disabled={busy}
              className="border-sky-300 text-sky-700 hover:bg-sky-50"
              data-testid="streak-rescue-freeze-btn"
            >
              {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Snowflake className="w-4 h-4 mr-1.5" />}
              Use weekly freeze
            </Button>
          ) : freezeStatus && !freezeStatus.weekly_freeze_available ? (
            <span className="text-[11px] text-slate-500 self-center" data-testid="streak-rescue-freeze-spent">
              ❄️ Freeze used · next one unlocks {freezeStatus.next_freeze_unlocks}
            </span>
          ) : null}
        </div>
      </div>
    </div>
  );
}
